import { useRef, useImperativeHandle, forwardRef, useEffect, useCallback } from 'react'
import './VideoUpload.css'

const VideoUpload = forwardRef(function VideoUpload({ 
  videoUrl, 
  onVideoSelect, 
  isAnalyzing,
  enablePoseOverlay = false,
  poseExtractor = null
}, ref) {
  const fileInputRef = useRef(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const animationFrameRef = useRef(null)
  const lastVideoTimeRef = useRef(-1)

  // Expose video element to parent via ref
  useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current,
    seekTo: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time
      }
    }
  }), [])

  // Pose detection loop
  const runPoseDetection = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !poseExtractor || !enablePoseOverlay) {
      return
    }

    const video = videoRef.current
    const canvas = canvasRef.current

    // Only run if video is playing
    if (video.paused || video.ended) {
      // Clear canvas when paused
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      return
    }

    // Match canvas size to video
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 480
    }

    // Only process new frames
    const startTimeMs = performance.now()
    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime

      try {
        poseExtractor.detectForVideoCallback(video, startTimeMs, (result) => {
          if (result.landmarks && result.landmarks.length > 0) {
            poseExtractor.drawPoseResults(canvas, result)
          } else {
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height)
            }
          }
        })
      } catch (err) {
        console.warn('Pose detection error:', err)
      }
    }

    // Continue loop
    animationFrameRef.current = requestAnimationFrame(runPoseDetection)
  }, [enablePoseOverlay, poseExtractor])

  // Handle video play/pause for pose detection
  useEffect(() => {
    const video = videoRef.current
    if (!video || !enablePoseOverlay || !poseExtractor) return

    const handlePlay = () => {
      lastVideoTimeRef.current = -1
      runPoseDetection()
    }

    const handlePause = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      // Clear canvas
      const canvas = canvasRef.current
      if (canvas) {
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
      }
    }

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handlePause)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [enablePoseOverlay, poseExtractor, runPoseDetection])

  // Clear canvas when overlay is disabled
  useEffect(() => {
    if (!enablePoseOverlay && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      }
    }
  }, [enablePoseOverlay])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('video/')) {
      onVideoSelect(file)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('video/')) {
      onVideoSelect(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="video-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {videoUrl ? (
        <div className="video-preview">
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            className="video-player"
          />
          {/* Pose overlay canvas */}
          {enablePoseOverlay && (
            <canvas
              ref={canvasRef}
              className="pose-overlay-canvas"
            />
          )}
          {/* Pose overlay indicator */}
          {enablePoseOverlay && poseExtractor && (
            <div className="pose-overlay-indicator">
              🦴 Pose Overlay Active
            </div>
          )}
          {isAnalyzing && (
            <div className="analyzing-overlay">
              <div className="spinner"></div>
              <p>Analyzing...</p>
            </div>
          )}
        </div>
      ) : (
        <div
          className="upload-area"
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="upload-content">
            <div className="upload-icon">📹</div>
            <h3>Upload Video</h3>
            <p>Click to browse or drag and drop your video here</p>
            <div className="upload-formats">
              Supports MP4, WebM, MOV, AVI
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default VideoUpload

